import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CreditCard, CheckCircle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function PaymentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { iframeToken, merchantOid, amount, credits, mock_mode } = location.state || {};
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!iframeToken || !merchantOid) {
      navigate('/credits');
      return;
    }

    // For mock mode, no need to load PayTR script
    if (mock_mode) {
      setLoading(false);
      return;
    }

    // Load PayTR iframe resizer script
    const script = document.createElement('script');
    script.src = 'https://www.paytr.com/js/iframeResizer.min.js';
    script.async = true;
    script.onload = () => {
      setLoading(false);
      // Initialize iframe resizer after iframe loads
      setTimeout(() => {
        if (window.iFrameResize) {
          window.iFrameResize({}, '#paytriframe');
        }
      }, 500);
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [iframeToken, merchantOid, navigate, mock_mode]);

  const handleTestPayment = async () => {
    setProcessing(true);
    try {
      const response = await api.post(`/paytr/test-payment/${merchantOid}`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Navigate to success page
        navigate(`/payment/success?merchant_oid=${merchantOid}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ödeme işlemi başarısız');
      console.error(error);
    } finally {
      setProcessing(false);
    }
  };

  if (!iframeToken || !merchantOid) {
    return null;
  }

  // Mock mode - test payment form
  if (mock_mode) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Test Ödeme Sayfası</CardTitle>
              <div className="text-sm text-slate-600 mt-2">
                <p><strong>Sipariş No:</strong> {merchantOid}</p>
                <p><strong>Kredi Miktarı:</strong> {credits} kredi</p>
                <p><strong>Tutar:</strong> {amount} ₺</p>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-2">Test Modu Aktif</h3>
                    <p className="text-sm text-blue-800">
                      PayTR entegrasyonu henüz tamamlanmadığı için test modunda çalışıyorsunuz. 
                      Gerçek bir ödeme yapılmayacak, krediler anında hesabınıza eklenecektir.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-6 mb-6">
                <div className="text-center">
                  <CreditCard className="h-16 w-16 mx-auto mb-4 text-green-600" />
                  <h3 className="text-xl font-bold text-green-900 mb-2">
                    Test Kartı ile Ödeme
                  </h3>
                  <p className="text-sm text-green-700 mb-4">
                    Aşağıdaki butona tıklayarak test ödemesini onaylayın
                  </p>
                  <div className="bg-white/50 rounded-lg p-4 mb-4">
                    <p className="text-2xl font-bold text-green-800">{amount} ₺</p>
                    <p className="text-sm text-green-600">{credits} kredi</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleTestPayment}
                disabled={processing}
                className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    İşleniyor...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Test Ödemesini Onayla
                  </>
                )}
              </Button>

              <p className="text-center text-xs text-slate-500 mt-4">
                ℹ️ Bu bir test ödemesidir. Gerçek para harcanmayacaktır.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Real PayTR mode
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Ödeme Sayfası</CardTitle>
            <div className="text-sm text-slate-600 mt-2">
              <p><strong>Sipariş No:</strong> {merchantOid}</p>
              <p><strong>Kredi Miktarı:</strong> {credits} kredi</p>
              <p><strong>Tutar:</strong> {amount} ₺</p>
            </div>
          </CardHeader>
          <CardContent>
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="ml-2 text-slate-600">Ödeme ekranı yükleniyor...</span>
              </div>
            )}
            <div className="relative">
              <iframe
                id="paytriframe"
                src={`https://www.paytr.com/odeme/guvenli/${iframeToken}`}
                frameBorder="0"
                scrolling="no"
                style={{
                  width: '100%',
                  minHeight: '600px',
                  border: 'none'
                }}
                onLoad={() => setLoading(false)}
              />
            </div>
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-slate-700">
                <strong>Test Kartı Bilgileri (Sandbox):</strong>
              </p>
              <ul className="text-sm text-slate-600 mt-2 space-y-1">
                <li>• Kart No: 4355 0843 5508 4358</li>
                <li>• Son Kullanma: 12/30</li>
                <li>• CVV: 000</li>
                <li>• Kart Sahibi: Herhangi bir isim</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
